import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { state, slug, updateSettings, regenerateSlugRemote } from '../store/store';
import { Layout } from '../components/Layout';
import type { MealType } from '../types';

export function SettingsPage() {
  const s = state.value!;
  const stateBytes = JSON.stringify(s).length;
  const stateMb = stateBytes / 1_000_000;
  const [showSafetyRef, setShowSafetyRef] = useState(false);
  const [copied, setCopied] = useState(false);
  const [familyName, setFamilyName] = useState(s.settings.family_name ?? '');
  useEffect(() => { setFamilyName(s.settings.family_name ?? ''); }, [s.settings.family_name]);

  async function copySlug() {
    try {
      await navigator.clipboard.writeText(slug.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  const settings = s.settings;
  function toggleMealType(mt: MealType) {
    const next = settings.meal_types_enabled.includes(mt)
      ? settings.meal_types_enabled.filter(x => x !== mt)
      : [...settings.meal_types_enabled, mt];
    void updateSettings({ meal_types_enabled: next });
  }

  function exportFullState() {
    const blob = new Blob([JSON.stringify(s, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mealplan-${slug.value}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function regenerateSlug() {
    if (!confirm('Generate a new family slug?\n\nThis creates a new URL. Every device currently bookmarked to the old URL will lose access until you re-share the new one. Your data is preserved on the new URL.')) return;
    try {
      const next = await regenerateSlugRemote();
      location.replace(`/h/${next}/settings`);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <Layout active="settings" slug={slug.value}>
      <div style={{ padding: 16, maxWidth: 720, margin: '0 auto' }}>
        <h2>Settings</h2>

        {stateMb > 2 && (
          <div style={{
            padding: 12, background: 'var(--amber-bg)', color: 'var(--amber-text)',
            borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 13,
          }}>
            Your data is {stateMb.toFixed(1)}MB. Consider removing photos from older recipes — large data slows sync.
          </div>
        )}

        <section style={card}>
          <h3 className="settings-card-head">Family</h3>
          <Row label="Family name">
            <input
              type="text"
              value={familyName}
              onInput={(e) => setFamilyName((e.currentTarget as HTMLInputElement).value)}
              onBlur={() => {
                if (familyName !== (s.settings.family_name ?? '')) {
                  void updateSettings({ family_name: familyName || undefined });
                }
              }}
              style={inputStyle}
            />
          </Row>
          <Row label="Family slug">
            <code style={{ background: 'var(--surface-card)', padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--ink)' }}>{slug.value}</code>
            <button onClick={copySlug} style={secBtn}>📋 Copy</button>
            {copied && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--sage-text)' }}>Copied!</span>}
            <button onClick={regenerateSlug} style={secBtn}>Regenerate</button>
          </Row>
        </section>

        <section style={card}>
          <h3 className="settings-card-head">Meal types</h3>
          {(['lunch','dinner','breakfast','snack'] as MealType[]).map(mt => (
            <label key={mt} style={{ display: 'block', marginBottom: 4 }}>
              <input
                type="checkbox" checked={settings.meal_types_enabled.includes(mt)}
                onChange={() => toggleMealType(mt)}
              /> {mt}
            </label>
          ))}
          <p style={{ fontStyle: 'italic', color: 'var(--plum-soft)', fontSize: 11, marginTop: 8, marginBottom: 0 }}>
            Breakfast & snack are experimental — recipes and planning are tuned for lunch & dinner.
          </p>
        </section>

        <section style={card}>
          <h3 className="settings-card-head">Banners</h3>
          <Row label="Show balance check">
            <input type="checkbox" checked={settings.show_balance_check} onChange={e => updateSettings({ show_balance_check: (e.target as HTMLInputElement).checked })} />
          </Row>
          <Row label="Show picky-eater nudge">
            <input type="checkbox" checked={settings.show_picky_eater_nudge} onChange={e => updateSettings({ show_picky_eater_nudge: (e.target as HTMLInputElement).checked })} />
          </Row>
          <Row label="Show safety notes">
            <input type="checkbox" checked={settings.show_safety_notes} onChange={e => updateSettings({ show_safety_notes: (e.target as HTMLInputElement).checked })} />
          </Row>
        </section>

        <section style={card}>
          <h3 className="settings-card-head">Exports / backup</h3>
          <button onClick={exportFullState} style={btn}>⤓ Download full state JSON</button>
        </section>

        <section style={card}>
          <h3 className="settings-card-head">Install on home screen</h3>
          <p style={{ color: 'var(--plum-soft)', fontSize: 13 }}>
            <strong>iOS:</strong> tap the Share button → Add to Home Screen.<br />
            <strong>Android Chrome:</strong> tap the install banner that appears, or browser menu → Install app.<br />
            <strong>Desktop Chrome / Edge:</strong> click the install icon in the address bar.
          </p>
        </section>

        <section style={card}>
          <h3 className="settings-card-head">Safety reference</h3>
          <button onClick={() => setShowSafetyRef(v => !v)} style={secBtn}>
            {showSafetyRef ? 'Hide' : 'Show'} choking-hazard reference
          </button>
          {showSafetyRef && <SafetyReference />}
        </section>
      </div>
    </Layout>
  );
}

function SafetyReference() {
  return (
    <div style={{ marginTop: 10, padding: 12, background: 'var(--sage)', borderRadius: 10, color: 'var(--sage-text)' }}>
      <strong>Avoid entirely (under age 4):</strong>
      <ul>
        <li>Whole nuts and seeds</li>
        <li>Popcorn</li>
        <li>Hard candy, gummies, jelly beans, marshmallows, gum</li>
        <li>Whole grain kernels</li>
        <li>Tough or large chunks of meat</li>
        <li>Crackers/bread with whole nuts or seeds embedded</li>
      </ul>
      <strong>Modify before serving:</strong>
      <ul>
        <li>Grapes, cherries, blueberries: halve or quarter lengthwise</li>
        <li>Cherry / grape tomatoes: quarter</li>
        <li>Hot dogs and sausages: quarter lengthwise then dice (better to skip until 4+)</li>
        <li>Raw carrots and apples: shred, slice paper-thin, or steam tender</li>
        <li>Raw celery: avoid strings; cook or shred finely</li>
        <li>Cheese: cube or shred (slice cheese sticks lengthwise)</li>
        <li>Nut/seed butters: spread thin, never in chunks or by the spoonful</li>
        <li>Whole olives: halve or chop</li>
        <li>Dried fruit: soft and chopped if served at all</li>
      </ul>
      <p style={{ fontSize: 11, opacity: 0.8 }}>Source: AAP HealthyChildren.org and the playbook. A 3-year-old's airway is roughly the diameter of a pencil.</p>
    </div>
  );
}

function Row(props: { label: string; children: any }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
    <span className="label-caps" style={{ minWidth: 140 }}>{props.label}</span>
    {props.children}
  </div>;
}

const card: JSX.CSSProperties = {
  background: 'white',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-lg)',
  marginBottom: 'var(--space-md)',
  boxShadow: 'var(--shadow-soft)',
};
const btn: JSX.CSSProperties = { padding: '12px 20px', height: 44, borderRadius: 'var(--radius-md)', background: 'var(--pink-primary)', color: 'var(--on-primary)', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' };
const secBtn: JSX.CSSProperties = { padding: '7px 12px', height: 32, borderRadius: 'var(--radius-sm)', background: 'white', color: 'var(--ink)', border: '1px solid var(--hairline)', fontSize: 12, fontWeight: 600, cursor: 'pointer', lineHeight: 1, marginLeft: 8 };
const inputStyle: JSX.CSSProperties = { padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)', background: 'white', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.4 };
